/**
 * Authentication Context for ABA Study Buddy
 * 
 * Provides authentication state and methods throughout the app using React Context.
 * Handles:
 * - User login and registration
 * - JWT token management (stored in localStorage)
 * - User session persistence
 * - Protected route authentication checks
 * 
 * Usage:
 *   const { user, login, logout, isAuthenticated } = useAuth();
 */

import React, { createContext, useState, useContext, useEffect } from 'react';
import { login as apiLogin, register as apiRegister, getMe } from '../api';

// Create context with null as default value
const AuthContext = createContext(null);

/**
 * AuthProvider Component
 * 
 * Wraps the entire app to provide authentication state and methods.
 * Should be placed at the root level in App.js.
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 */
export const AuthProvider = ({ children }) => {
  // State management
  const [user, setUser] = useState(null);  // Current user object (null if not logged in)
  const [loading, setLoading] = useState(true);  // Loading state during auth check
  const [token, setToken] = useState(localStorage.getItem('token'));  // JWT token

  const getAuthErrorMessage = (error, fallbackMessage) => {
    const responseData = error?.response?.data;

    if (typeof responseData === 'string' && responseData.trim()) {
      return responseData;
    }

    if (typeof responseData?.detail === 'string' && responseData.detail.trim()) {
      return responseData.detail;
    }

    if (Array.isArray(responseData?.detail) && responseData.detail.length > 0) {
      return responseData.detail
        .map((item) => item?.msg)
        .filter(Boolean)
        .join(', ');
    }

    if (typeof responseData?.message === 'string' && responseData.message.trim()) {
      return responseData.message;
    }

    if (error?.message === 'Network Error') {
      return 'Unable to reach the server. Please check your connection and try again.';
    }

    if (error?.response?.status) {
      return `Request failed (${error.response.status}). Please try again.`;
    }

    return fallbackMessage;
  };

  useEffect(() => {
    // Check if user is logged in whenever token changes
    // If token exists, validate it by fetching user data
    if (token) {
      fetchUser(token);
    } else {
      setUser(null);
      setLoading(false);
    }
  }, [token]);

  /**
   * Fetch current user data from backend using stored token
   * Called on app load to restore user session
   */
  const fetchUser = async (tokenToVerify) => {
    try {
      const userData = await getMe();
      setUser(userData);
    } catch (error) {
      console.error('Error fetching user:', error);
      // Only clear auth if this failure belongs to the currently active token
      if (tokenToVerify === localStorage.getItem('token')) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Login user with email and password
   * 
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @returns {Promise<Object>} { success: boolean, error?: string }
   * 
   * On success:
   * - Stores JWT token in localStorage
   * - Sets user state
   * - Sets token state
   */
  const login = async (email, password) => {
    try {
      const data = await apiLogin(email, password);
      localStorage.setItem('token', data.access_token);
      setUser(data.user);
      setToken(data.access_token);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: getAuthErrorMessage(error, 'Login failed')
      };
    }
  };

  /**
   * Register new user account
   * 
   * @param {string} firstName - User's first name
   * @param {string} email - User's email
   * @param {string} password - User's password (min 6 chars)
   * @returns {Promise<Object>} { success: boolean, error?: string }
   * 
   * On success:
   * - Automatically logs user in
   * - Stores JWT token
   * - Initializes user progress in database
   */
  const register = async (firstName, email, password) => {
    try {
      const data = await apiRegister(firstName, email, password);
      localStorage.setItem('token', data.access_token);
      setUser(data.user);
      setToken(data.access_token);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: getAuthErrorMessage(error, 'Registration failed')
      };
    }
  };

  /**
   * Logout current user
   * Clears all authentication state and localStorage
   */
  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    setLoading(false);
  };

  // Context value provided to all children
  const value = {
    user,  // Current user object or null
    token,  // Current JWT token or null
    loading,  // True while checking authentication on mount
    login,  // Login function
    register,  // Register function
    logout,  // Logout function
    isAuthenticated: !!user  // Boolean: true if user is logged in
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * useAuth Hook
 * 
 * Custom hook to access authentication context.
 * Must be used within an AuthProvider.
 * 
 * @returns {Object} Authentication context value
 * @throws {Error} If used outside AuthProvider
 * 
 * Example:
 *   const { user, isAuthenticated, logout } = useAuth();
 *   if (isAuthenticated) {
 *     return <div>Welcome {user.username}!</div>
 *   }
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
